import {
  AppleFilled,
  AppleOutlined,
  AppstoreAddOutlined,
  CopyFilled,
  CopyOutlined,
  GitlabFilled,
  LinuxOutlined,
  LoadingOutlined,
  WindowsFilled,
  WindowsOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Divider,
  Grid,
  Modal,
  notification,
  Pagination,
  Progress,
  Row,
  Space,
  Tabs,
  Tooltip,
  Typography,
} from 'antd';
import { Project } from 'osep-generator-service-client-v1';
import { Page } from 'osep-react-antd';
import React, { useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { SiJira } from 'react-icons/si';
import { useNavigate } from 'react-router';
import SyntaxHighlighter from 'react-syntax-highlighter';
import { docco } from 'react-syntax-highlighter/dist/esm/styles/hljs';
import packageJson from '../../package.json';
import { CliData } from '../cliDataSchema';
import OpenPipeline from '../components/AdminView/ShowPipeline';
import DeleteProject from '../components/DeleteProject';
import config from '../config';
import useCli from '../hooks/query/useCli';
import useProjects from '../hooks/query/useProjects';
import useIsAdmin from '../hooks/useIsAdmin';
import findImageByProjectNameOrType from '../utils/findImageByProjectNameOrType';
import mapGitLabPipelineStatus from '../utils/mapGitLabPipelineStatus';
import ErrorPage from './ErrorPage';

const ProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useIsAdmin();
  const [{ page, pageSize }, setPage] = React.useState({
    page: 1,
    pageSize: 10,
  });
  const [modalContent, setModalContent] = useState<
    | {
        type: 'linux' | 'windows' | 'apple';
        icon: React.ReactElement;
        project: Project;
      }
    | undefined
  >(undefined);

  const screen = Grid.useBreakpoint();

  const { data: cliData, isLoading: isCliLoading, error: cliError } = useCli();

  const cliDataTyped = cliData as CliData | undefined;

  const {
    data: projectsData,
    error: projectsError,
    isLoading: isProjectsLoading,
  } = useProjects({ pageIndex: page - 1, pageSize }, 3000);

  const getSystemIcon = (project: Project) => {
    const platform = navigator.platform.toLowerCase();

    if (platform.includes('mac')) {
      return (
        <AppleFilled
          key="apple"
          onClick={() =>
            setModalContent({
              type: 'apple',
              project,
              icon: <AppleOutlined />,
            })
          }
        />
      );
    } else if (platform.includes('win')) {
      return (
        <WindowsFilled
          key="windows"
          onClick={() =>
            setModalContent({
              type: 'windows',
              project,
              icon: <WindowsOutlined />,
            })
          }
        />
      );
    } else if (platform.includes('linux')) {
      return (
        <LinuxOutlined
          key="linux"
          onClick={() =>
            setModalContent({
              type: 'linux',
              project,
              icon: <LinuxOutlined />,
            })
          }
        />
      );
    }
    return (
      <WindowsFilled
        key="windows-default"
        onClick={() =>
          setModalContent({
            type: 'windows',
            project,
            icon: <WindowsOutlined />,
          })
        }
      />
    );
  };

  if (projectsError) {
    return <ErrorPage error={projectsError} />;
  }

  if (cliError) {
    return <ErrorPage error={cliError} />;
  }

  const commands = cliDataTyped?.commands.find((c) => c.name === 'create')
    ?.commands;

  const EllipsisMiddle: React.FC<{ suffixCount: number; children: string }> = ({
    suffixCount,
    children,
  }) => {
    const start = children.slice(0, children.length - suffixCount);
    const suffix = children.slice(-suffixCount).trim();

    return (
      <Typography.Text
        type="secondary"
        copyable
        ellipsis={{
          tooltip: `${start}${suffix}`,
        }}
        style={{ paddingBottom: 24 }}
      >
        {start + suffix}
      </Typography.Text>
    );
  };

  const modalContentBashString = modalContent
    ? `mkdir -p ~/${modalContent.project.gitlabGroupFullPath}/${modalContent.project.gitlabProjectPath}
cd ~/${modalContent.project.gitlabGroupFullPath}/${modalContent.project.gitlabProjectPath}
git clone git@gitlab.com:${modalContent.project.gitlabGroupFullPath}/${modalContent.project.gitlabProjectPath}.git .`
    : '';

  const modalContentPowerShellString = modalContent
    ? `New-Item -ItemType Directory -Path "$HOME\\${modalContent.project.gitlabGroupFullPath.replace(
        /\//g,
        '\\',
      )}\\${modalContent.project.gitlabProjectPath}" -Force
Set-Location -Path "$HOME\\${modalContent.project.gitlabGroupFullPath.replace(
        /\//g,
        '\\',
      )}\\${modalContent.project.gitlabProjectPath}"
git clone git@gitlab.com:${modalContent.project.gitlabGroupFullPath}/${
        modalContent.project.gitlabProjectPath
      }.git .`
    : '';

  return (
    <Page
      title="Projects"
      isLoading={isProjectsLoading || isCliLoading}
      footer={<Typography.Text disabled>{packageJson.version}</Typography.Text>}
      onBack={() => navigate('/')}
    >
      <Button
        type="primary"
        onClick={() => navigate(`/projects/add`)}
        style={{ width: screen.xs ? '100%' : 'auto' }}
        size="large"
        icon={<AppstoreAddOutlined />}
      >
        Add New Project
      </Button>
      <Divider />
      <Row gutter={[16, 16]}>
        {projectsData?.projects.map((project) =>
          project.showProject !== false ? (
            <Col key={project.id} xs={24} sm={24} md={12} lg={8} xl={6}>
              <Card
                title={
                  <>
                    <Typography.Title level={4}>
                      {project.gitlabProjectPath}
                    </Typography.Title>
                    <EllipsisMiddle
                      suffixCount={project.gitlabProjectPath.length + 1}
                    >
                      {`${project.gitlabGroupFullPath}/${project.gitlabProjectPath}`}
                    </EllipsisMiddle>
                  </>
                }
                key={project.id}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}
                actions={[
                  ...(project.generations?.[0]?.status === 'SUCCESS'
                    ? [
                        <Tooltip title="Open GitLab Project">
                          <GitlabFilled
                            key="open-gitlab"
                            onClick={() =>
                              window
                                .open(
                                  `${
                                    config.gitlabUrl
                                  }/${project.gitlabGroupFullPath.replace(
                                    /\/$/,
                                    '',
                                  )}/${project.gitlabProjectPath}`,
                                  '_blank',
                                )
                                ?.focus()
                            }
                          />
                        </Tooltip>,
                        <Tooltip title="Clone Gitlab Project">
                          {getSystemIcon(project)}
                        </Tooltip>,
                      ]
                    : project.generations?.[0]?.status === 'ERROR' &&
                        !project.generations?.[0]?.ticketUrl
                      ? [<LoadingOutlined key="loading" />]
                      : project.generations?.[0]?.status === 'ERROR' &&
                          project.generations?.[0]?.ticketUrl
                        ? [
                            <Tooltip title="Open Jira Ticket">
                              <span
                                key="jira"
                                onClick={() =>
                                  window
                                    .open(
                                      project.generations[0].ticketUrl,
                                      '_blank',
                                    )
                                    ?.focus()
                                }
                                style={{
                                  cursor: 'pointer',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                }}
                              >
                                {SiJira({})}
                              </span>
                            </Tooltip>,

                            <Tooltip title="Copy Ticket URL">
                              <CopyFilled
                                key="copy"
                                onClick={() =>
                                  navigator.clipboard
                                    .writeText(
                                      project.generations[0].ticketUrl ?? '',
                                    )
                                    .then(() => {
                                      notification.open({
                                        message: 'Copied!',
                                        description: (
                                          <span>
                                            <a
                                              href={
                                                project.generations[0].ticketUrl
                                              }
                                              target="_blank"
                                              rel="noreferrer"
                                            >
                                              {project.generations[0].ticketUrl}
                                            </a>
                                          </span>
                                        ),
                                        placement: 'top',
                                      });
                                    })
                                }
                              />
                            </Tooltip>,
                          ]
                        : []),
                  ...(isAdmin ||
                  ['RUNNING', 'ERROR', 'SUCCESS'].includes(
                    project.generations?.[0]?.status ?? '',
                  )
                    ? [<OpenPipeline key="pipeline" project={project} />]
                    : []),
                  <DeleteProject project={project} />,
                ].filter(Boolean)}
                cover={
                  <Space style={{ textAlign: 'center' }}>
                    {project.generations && project.generations.length > 0 ? (
                      <img
                        src={findImageByProjectNameOrType(
                          commands?.find((c) => c.name === project.projectType)
                            ?.osep_generator_ui_project_group ?? '',
                        )}
                        style={{
                          height: 100,
                          width: 'auto',
                          marginTop: 20,
                          filter:
                            project.generations[0].status === 'SUCCESS'
                              ? undefined
                              : 'grayscale(100%)',
                        }}
                        alt=""
                      />
                    ) : (
                      <img
                        src={findImageByProjectNameOrType('x')}
                        style={{
                          height: 100,
                          width: 'auto',
                          marginTop: 20,
                        }}
                        alt=""
                      />
                    )}
                  </Space>
                }
              >
                <Card.Meta
                  description={
                    <>
                      {project.generations && project.generations.length > 0 ? (
                        [project.generations[0].status]
                          .map(mapGitLabPipelineStatus)
                          .map(({ color, text, status, progressStatus }) => (
                            <Progress
                              percent={project.generations[0].progress}
                              key={project.id}
                              status={progressStatus}
                              style={{ paddingTop: 24, width: '100%' }}
                              size={'small'}
                            >
                              {text}
                            </Progress>
                          ))
                      ) : (
                        <Typography.Text
                          type="secondary"
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 50,
                            fontWeight: 'bold',
                            fontStyle: 'italic',
                            textAlign: 'center',
                            color: 'red',
                          }}
                        >
                          The Generation could not be started
                        </Typography.Text>
                      )}
                    </>
                  }
                />
              </Card>
            </Col>
          ) : null,
        )}
      </Row>

      <Pagination
        defaultCurrent={page}
        total={projectsData?.totalCount}
        pageSize={pageSize}
        onChange={(page, pageSize) => setPage({ page, pageSize })}
        pageSizeOptions={['10', '20', '50', '100']}
        showSizeChanger
        align="center"
        style={{ marginTop: 24 }}
      />
      <Modal
        open={!!modalContent}
        onCancel={() => setModalContent(undefined)}
        footer={
          <>
            <CopyToClipboard
              text={
                modalContent?.type === 'windows'
                  ? modalContentPowerShellString
                  : modalContentBashString
              }
            >
              <Button type="primary" icon={<CopyOutlined />}>
                Copy
              </Button>
            </CopyToClipboard>
            <Button onClick={() => setModalContent(undefined)}>Close</Button>
          </>
        }
        width={'auto'}
      >
        <Tabs
          activeKey={modalContent?.type}
          items={[
            {
              key: 'apple',
              label: <AppleFilled />,
              children: (
                <>
                  <Typography.Paragraph>
                    <SyntaxHighlighter language="bash" style={docco}>
                      {modalContentBashString}
                    </SyntaxHighlighter>
                  </Typography.Paragraph>
                </>
              ),
            },
            {
              key: 'windows',
              label: <WindowsFilled />,
              children: (
                <>
                  <Typography.Paragraph>
                    <SyntaxHighlighter language="bash" style={docco}>
                      {modalContentPowerShellString}
                    </SyntaxHighlighter>
                  </Typography.Paragraph>
                </>
              ),
            },
            {
              key: 'linux',
              label: <LinuxOutlined />,
              children: (
                <>
                  <Typography.Paragraph>
                    <SyntaxHighlighter language="bash" style={docco}>
                      {modalContentBashString}
                    </SyntaxHighlighter>
                  </Typography.Paragraph>
                </>
              ),
            },
          ]}
          onChange={(key: any) =>
            setModalContent(
              modalContent ? { ...modalContent, type: key } : modalContent,
            )
          }
          size="large"
        />
      </Modal>
    </Page>
  );
};

export default ProjectsPage;
